import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import PageHeader from "../components/PageHeader";
import StatChip from "../components/StatChip";
import Loader from "../components/Loader";
import SelectInput from "../components/SelectInput";
import PositionSelect from "../components/PositionSelect";
import { getCurrentRole, isAdminRole, isActiveUser } from "../utils/common";
import { getTeamColors } from "../utils/badgeColors";

import {
  teamListApis,
  teamMembersApis,
  upsertTeamApis,
  upsertTeamMemberApis,
  setTeamMemberManagerApis,
  designationsApis,
  setUserDesignationApis,
  reorderTeamsApis,
} from "../redux/team/teamActions";
import { usersApis } from "../redux/users/userActions";
import { selectUsers } from "../redux/users/userSelectors";
import { selectTeams, selectTeamsStatus } from "../redux/team/teamSelectors";
import "../assets/css/teams.css";


const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/);
  if (!parts[0]) return "?";
  return parts.length === 1
    ? parts[0].slice(0, 2).toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* A first guess at the short name, from the name being typed.
 *
 * Every team already has one and they are all shaped the same way — the first
 * word, upper case, cut short. Typing "Data Migration" and then typing "DATA"
 * underneath it is asking somebody to repeat themselves, and a field nobody
 * wants to fill is a field people put "X" in.
 *
 * Only ever a SUGGESTION: the moment the field is edited by hand it is left
 * alone, because the good short names are the ones a person chose. Database is
 * DB and Testing is QC — no rule was going to produce those. */
const deriveShort = (teamName) =>
  String(teamName || "")
    .trim()
    .split(/\s+/)[0]
    .replace(/[^a-z0-9]/gi, "")
    .slice(0, 4)
    .toUpperCase();

/**
 * The create / rename form, as a CARD IN THE GRID rather than a panel above it.
 *
 * It used to be a full-width slab between the header and the cards. Three
 * things were wrong with that: opening it shoved every team down the page, the
 * team-name input ran the entire width of the window for a two-word answer, and
 * the thing being made looked nothing like the thing it would become.
 *
 * Here the form occupies exactly the cell the team will occupy, so nothing
 * moves when it opens, the fields are the width of the answer, and the top of
 * the card is a live preview of the real one — the coloured short-name pill
 * beside the name. That pill is on every task badge in the system, which is
 * worth seeing before it is committed to rather than after.
 *
 * Renaming flips the team's OWN card, in place, for the same reason.
 */
const TeamFormCard = ({ form, setForm, busy, onCancel, onSave }) => {
  const isRename = Boolean(form.teamCode);
  const teamName = form.teamName.trim();
  const shortName = form.shortName.trim();
  const valid = Boolean(teamName && shortName);

  /* Coloured from the name as it is typed, so the accent stripe and the pill
     are already the colour the finished card will be. */
  const colors = getTeamColors(teamName || form.original || "");

  return (
    <form
      className="tm-card tm-card--form"
      style={{ "--tm-accent": colors.text }}
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !busy) onSave();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      <div className="tm-card__head">
        <span className="tm-card__short tm-card__short--preview">
          {shortName || "··"}
        </span>
        <h6 className="tm-card__name" title={teamName}>
          {teamName || (isRename ? form.original : "New team")}
        </h6>
      </div>

      <div className="tm-fields">
        <label className="tm-f">
          <span>Team name</span>
          <input
            className="tm-input"
            autoFocus
            maxLength={100}
            placeholder="Data Migration"
            value={form.teamName}
            onChange={(e) => {
              const value = e.target.value;
              setForm((f) => ({
                ...f,
                teamName: value,
                shortName: f.shortTouched ? f.shortName : deriveShort(value),
              }));
            }}
          />
        </label>

        <label className="tm-f">
          <span>
            Short name
            <em>shown on every task</em>
          </span>
          <input
            className="tm-input tm-input--short"
            maxLength={20}
            placeholder="DATA"
            value={form.shortName}
            /* Upper-cased as you type: every existing short name is upper
               case and they sit side by side on task badges, where one
               lower-case one looks like a mistake. */
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                shortName: e.target.value.toUpperCase(),
                shortTouched: true,
              }))
            }
          />
        </label>
      </div>

      <p className="tm-hint">
        {isRename
          ? "The short name is on every task badge, so changing it changes them all."
          : "Gets a dashboard card and two-way routes with every team. Add people from its People button once it exists."}
      </p>

      <div className="tm-actions">
        <button type="button" className="tm-btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="tm-btn-solid"
          disabled={busy || !valid}
          /* Says WHY it is disabled. A greyed-out button with no explanation
             is the same as a broken one to the person pressing it. */
          title={valid ? "" : "Both a name and a short name are needed"}
        >
          {busy ? "Saving…" : isRename ? "Save" : "Create team"}
        </button>
      </div>
    </form>
  );
};

/**
 * Teams — everything that CHANGES a team.
 *
 * Its name, its short name, its order, and who is in it. All of it used to be
 * on the Team Tree, inside a canvas that pans sideways: a form opening in the
 * middle of a centred chart shoves every other card out from under the cursor,
 * and the buttons themselves scrolled off screen. The page that owned the
 * edits was the page they could not be performed on.
 *
 * A plain grid of cards has none of those problems, and it sits in the menu
 * beside the other masters, where somebody looking for "where do I change a
 * team" would actually look.
 *
 * The tree still exists and is worth keeping — it reads. Rank order inside a
 * team, how loaded each one is, where work flows next. Reading the shape of an
 * organisation and changing it are different jobs, and only one of them wants
 * a chart.
 */
const Teams = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const role = getCurrentRole();
  const canEdit = isAdminRole(role);

  const teams = useSelector(selectTeams);
  const teamsStatus = useSelector(selectTeamsStatus);
  const membersByTeam = useSelector((s) => s.team.membersByTeam);
  const designations = useSelector((s) => s.team.designations);
  const usersData = useSelector(selectUsers);

  /* null = closed. An object = the form, holding either a new team or the one
     being renamed; teamCode tells the two apart, and it is what the endpoint
     switches on too. */
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  /* Whose roster is open, and who is picked in its Add box. Rosters moved
     here from the Team Tree: the tree is a picture of the organisation, and
     the controls for changing it were buried inside a canvas that pans
     sideways — which is why nobody could find them. Teams owns every edit
     now; the tree reads. */
  const [peopleFor, setPeopleFor] = useState(null);
  const [pickedUser, setPickedUser] = useState("");

  /* Set when the person being added already belongs to a team, so the admin
     can say whether this is a move or a second team. Null the rest of the
     time — most adds have nothing to resolve. */
  const [conflict, setConflict] = useState(null);

  const activeTeams = useMemo(
    () => (teams || []).filter((t) => t.Status !== 0),
    [teams],
  );

  useEffect(() => {
    dispatch(teamListApis({}));
    // The pool the Add box draws from. One call, up front: asking per roster
    // would refetch the whole staff list every time a card is opened.
    dispatch(usersApis({ currentPage: 1, pageSize: 1000 }));
    // Manager / Team Leader / Senior / Junior Developer. A short static list,
    // fetched once rather than per dialog.
    dispatch(designationsApis());
  }, [dispatch]);

  const usersList = useMemo(() => {
    const d = usersData?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d?.Data)) return d.Data;
    return [];
  }, [usersData]);

  /* Head counts come from each team's roster rather than tbl_Team, so the
     number here matches the one on the tree rather than nearly matching it. */
  useEffect(() => {
    activeTeams.forEach((t) => {
      if (!membersByTeam[t.TeamCode]) {
        dispatch(teamMembersApis({ teamCode: t.TeamCode }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, activeTeams.length]);

  /* The real roster only.
   *
   * /team/members appends the all-access accounts to every team it is asked
   * about, flagged IsAllTeams — right for an assignee dropdown, where you
   * genuinely can hand a task to an admin. Wrong here: they would appear under
   * every team, and the count on the card would stop matching tbl_UserTeam,
   * which is what "who is in this team" means.
   *
   * A copy, because Array.prototype.sort mutates and this array belongs to the
   * store: sorting it in place would reorder redux state from a render. */
  const membersOf = (teamCode) =>
    (membersByTeam[teamCode] || [])
      .filter((m) => !m.IsAllTeams)
      .slice()
      /* By POSITION, then by name. Sorting on the permission role put every
         ADMIN above every developer regardless of what either of them does,
         which is a ranking of what an account may click rather than of
         seniority. Anyone with no position sorts last: an unset field is not
         evidence either way, and putting them on top would be a claim. */
      .sort(
        (a, b) =>
          (a.DesignationRank ?? 999) - (b.DesignationRank ?? 999) ||
          String(a.UserName || "").localeCompare(String(b.UserName || "")),
      );

  const memberCount = (teamCode) => membersOf(teamCode).length;

  /* Who reaches every team by role, taken from the server's own IsAllTeams
     flag rather than from a role test here. An ADMIN with a real tbl_UserTeam
     row comes back as an ordinary member — testing the role instead would make
     every ADMIN unaddable, including one removing themselves from a team and
     then finding no way back in. */
  const allAccessCodes = useMemo(() => {
    const first = activeTeams[0]?.TeamCode;
    return new Set(
      (membersByTeam[first] || [])
        .filter((m) => m.IsAllTeams)
        .map((m) => m.UserCode),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeams, membersByTeam]);

  /* Only the people not already in it, and never the all-access accounts:
     they hold every team by role, so "adding" one would write a row that
     changes nothing. */
  const candidatesFor = (teamCode) => {
    const inTeam = new Set(membersOf(teamCode).map((m) => m.UserCode));
    return usersList
      .filter((u) => isActiveUser(u) && !inTeam.has(u.UserCode))
      .filter(
        (u) =>
          !allAccessCodes.has(u.UserCode) &&
          /* Backstop for the moment before the first roster lands, when
             allAccessCodes is still empty. SUPER_ADMIN is the role the
             procedure treats as all-access, so it is the one to name. */
          String(u.Role || "").toUpperCase() !== "SUPER_ADMIN",
      )
      .map((u) => {
        /* The teams this person is ALREADY in, on the option itself.
         *
         * Adding somebody to a second team is a real and common thing here —
         * Subash and Jayasurya are in Support and Testing both — but it is
         * also very often a move being made by mistake. The picker used to
         * show a name and an email, which cannot tell those apart: you find
         * out afterwards, from the tree, that somebody is now in three teams.
         *
         * Free to compute — every team's roster is already in the store for
         * the cards behind this modal. */
        const already = activeTeams
          .filter((t) =>
            membersOf(t.TeamCode).some((m) => m.UserCode === u.UserCode),
          )
          .map((t) => t.TeamName);

        return {
          value: u.UserCode,
          label: u.UserName,
          /* Email is demoted to the meta line and the teams lead it: which
             teams somebody is in is what decides this click, and the address
             is only ever there to tell two people with one name apart. */
          meta: already.length
            ? `${already.join(" · ")}${u.EMail ? `  ·  ${u.EMail}` : ""}`
            : u.EMail || "No team yet",
          teamCount: already.length,
        };
      });
  };

  /* Which teams somebody is already in. Read from the rosters already in the
     store, so asking this costs no request. */
  const teamsOfUser = (userCode) =>
    activeTeams.filter((t) =>
      membersOf(t.TeamCode).some((m) => m.UserCode === Number(userCode)),
    );

  const refreshTeam = (teamCode) => {
    dispatch(teamListApis({}));
    dispatch(teamMembersApis({ teamCode }));
  };

  /* Put somebody in a team.
   *
   * Being in two teams is legitimate, so this cannot just refuse. But it is
   * equally often a MOVE somebody is performing, and doing that as an add
   * leaves the person on their old team's roster still receiving its work,
   * with nothing on screen saying so. The two intentions look identical at the
   * moment of clicking, so the only way to tell them apart is to ask — and
   * only when there is something to ask about. */
  const addMember = async (teamCode, mode = "ask") => {
    if (!pickedUser) return;

    const existing = teamsOfUser(pickedUser);

    if (mode === "ask" && existing.length > 0) {
      const person = usersList.find((u) => u.UserCode === Number(pickedUser));
      setConflict({
        userCode: Number(pickedUser),
        userName: person?.UserName || "This person",
        teamCode,
        teamName:
          activeTeams.find((t) => t.TeamCode === teamCode)?.TeamName || "",
        existing,
      });
      return;
    }

    setBusy(true);

    /* Move: leave the old teams first. Status 0 rather than a delete — the
       row is history, and sp_TeamMember_AddEdit reactivates it if they ever
       come back, which is why rejoining a team you once left works at all.

       Sequential on purpose: these are writes to the same person's rows, and
       firing them together is how two of them end up interleaved. */
    if (mode === "move") {
      for (const t of existing) {
        if (t.TeamCode === teamCode) continue;
        await dispatch(
          upsertTeamMemberApis({
            userCode: Number(pickedUser),
            teamCode: t.TeamCode,
            isPrimary: false,
            status: 0,
          }),
        );
      }
    }

    const res = await dispatch(
      upsertTeamMemberApis({
        userCode: Number(pickedUser),
        teamCode,
        // Which team is somebody's primary is a deliberate choice, made on the
        // Users screen. Joining one does not silently become that.
        isPrimary: false,
        status: 1,
      }),
    );
    setBusy(false);

    if (upsertTeamMemberApis.fulfilled.match(res)) {
      const name =
        activeTeams.find((t) => t.TeamCode === teamCode)?.TeamName || "the team";
      toast.success(mode === "move" ? `Moved to ${name}.` : `Added to ${name}.`);
      setConflict(null);
      setPickedUser("");

      /* Both ends of a move have changed, so refresh every team that was
         touched — refreshing only the destination leaves the old team's card
         still counting somebody who has left it. */
      refreshTeam(teamCode);
      if (mode === "move") {
        existing.forEach((t) => dispatch(teamMembersApis({ teamCode: t.TeamCode })));
      }
    }
  };

  /* Put one person under another, or lift them back to the top.
   *
   * Every rule about what is a legal line lives in sp_TeamMember_SetManager —
   * same team, not themselves, no cycle. The thunk shows the server's own
   * sentence when it refuses, so there is nothing to say here beyond
   * reloading the roster the answer changed. */
  const setManager = async (teamCode, userCode, reportsTo) => {
    setBusy(true);
    const res = await dispatch(
      setTeamMemberManagerApis({
        userCode,
        teamCode,
        reportsTo: reportsTo === "" || reportsTo == null ? null : Number(reportsTo),
      }),
    );
    setBusy(false);
    if (setTeamMemberManagerApis.fulfilled.match(res)) refreshTeam(teamCode);
  };

  /* What somebody does — their position in the company.
   *
   * Saved against the PERSON, so it changes what every team's chart calls
   * them. That is deliberate and worth knowing while clicking: a job title is
   * not a per-team fact, and the alternative would let the same person be
   * Senior in one team and Junior in another. */
  const setDesignation = async (teamCode, userCode, designationCode) => {
    setBusy(true);
    const res = await dispatch(
      setUserDesignationApis({
        userCode,
        designationCode:
          designationCode === "" || designationCode == null
            ? null
            : Number(designationCode),
      }),
    );
    setBusy(false);
    /* Every team's roster, not just this one — the person may be in two, and
       the other one is now showing a title the server has moved on from. */
    if (setUserDesignationApis.fulfilled.match(res)) {
      dispatch(teamListApis({}));
      activeTeams.forEach((t) => dispatch(teamMembersApis({ teamCode: t.TeamCode })));
    }
  };

  /* The manager's NAME, worked out from the roster already on screen.
   *
   * The procedure used to send a ReportsToName alongside ReportsTo, and that
   * column has now been dropped twice by rewrites of sp_TeamMember_GetAll —
   * each time silently, because a missing field reads as "no manager" rather
   * than as an error. The roster holds every member of this team, so the name
   * is one lookup away and needs no column at all. One less thing to lose.
   *
   * Undefined for a ReportsTo pointing at somebody no longer in the team,
   * which is the same answer the chart gives: they hang at the top. */
  const managerNameOf = (teamCode, member) =>
    member?.ReportsTo
      ? membersOf(teamCode).find((m) => m.UserCode === member.ReportsTo)?.UserName
      : null;

  /* Who this person could sit under: everybody else in the team.
   *
   * Their own descendants are left in — the server refuses a cycle with a
   * sentence that explains it, and filtering the list silently instead would
   * leave somebody wondering why a name they can see on the chart is missing
   * from the box. */
  const managerOptions = (teamCode, member) => [
    { value: "", label: "— Top of the team —" },
    ...membersOf(teamCode)
      .filter((m) => m.UserCode !== member.UserCode)
      .map((m) => ({ value: m.UserCode, label: m.UserName })),
  ];

  const removeMember = async (teamCode, member, teamName) => {
    if (
      !window.confirm(
        `Remove ${member.UserName} from ${teamName}?\n\nThey keep every task already assigned to them — this only stops new work in this team reaching them.`,
      )
    )
      return;

    setBusy(true);
    const res = await dispatch(
      upsertTeamMemberApis({
        userCode: member.UserCode,
        teamCode,
        isPrimary: false,
        status: 0,
      }),
    );
    setBusy(false);

    if (upsertTeamMemberApis.fulfilled.match(res)) {
      toast.success(`${member.UserName} removed from ${teamName}.`);
      refreshTeam(teamCode);
    }
  };

  const totalPeople = useMemo(() => {
    const seen = new Set();
    activeTeams.forEach((t) =>
      (membersByTeam[t.TeamCode] || [])
        .filter((m) => !m.IsAllTeams)
        .forEach((m) => seen.add(m.UserCode)),
    );
    return seen.size;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeams, membersByTeam]);

  const openNew = () =>
    setForm({ teamName: "", shortName: "", shortTouched: false });

  const save = async () => {
    const teamName = form.teamName.trim();
    const shortName = form.shortName.trim();
    if (!teamName || !shortName) return;

    setBusy(true);
    const res = await dispatch(
      upsertTeamApis({
        ...(form.teamCode ? { teamCode: form.teamCode } : {}),
        teamName,
        shortName,
      }),
    );
    setBusy(false);

    if (upsertTeamApis.fulfilled.match(res)) {
      toast.success(
        form.teamCode
          ? teamName + " updated."
          : teamName + " created — it has a dashboard card and can be given work straight away.",
      );
      setForm(null);
      dispatch(teamListApis({}));
    }
    /* No else. The procedure refuses a duplicate name or short name with a
       sentence written for the person typing, the thunk has already shown it,
       and the form stays open with their text still in it. */
  };

  /* Order decides the left-to-right order on the tree and the dashboard.
     Sends the whole list, not "move this one" — see sp_Team_Reorder for why a
     swap is the wrong shape. */
  const move = async (teamCode, delta) => {
    const order = activeTeams.map((t) => t.TeamCode);
    const from = order.indexOf(teamCode);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= order.length) return;

    order.splice(to, 0, order.splice(from, 1)[0]);
    setBusy(true);
    const res = await dispatch(reorderTeamsApis({ teamCodes: order }));
    setBusy(false);
    if (reorderTeamsApis.fulfilled.match(res)) dispatch(teamListApis({}));
  };

  const formProps = {
    form,
    setForm,
    busy,
    onCancel: () => setForm(null),
    onSave: save,
  };

  /* Admins only, and it stops at the door rather than showing a disabled copy.
   *
   * Every control here writes: POST /team and POST /team/member are authorized
   * for SUPER_ADMIN and ADMIN alone, so for anybody else this page is a set of
   * buttons the server will refuse. It used to render read-only for them,
   * which answers "what is in the teams" — but Team Tree answers that better
   * and is open to everyone, so a second, worse copy of it is not worth the
   * confusion of a screen you can look at but not use.
   *
   * The menu already hides it. This is the same rule again for anybody who
   * arrives by typing the URL or following an old link, because a hidden menu
   * item is not a permission check.
   *
   * After every hook, never before: bailing out earlier would call a different
   * number of hooks on different renders. */
  if (!canEdit) {
    return (
      <div className="report-container p-2 p-md-4">
        <PageHeader
          icon={<i className="bi bi-people-fill"></i>}
          title="Teams"
          subtitle="Creating and naming teams is an admin action"
        />
        <div className="tm-denied">
          <i className="bi bi-shield-lock" />
          <h6>Admins only</h6>
          <p>
            You are signed in as <strong>{role || "USER"}</strong>. Creating
            teams and changing who is in them is restricted to administrators.
          </p>
          <button
            type="button"
            className="tm-btn-solid"
            onClick={() => navigate("/team-tree")}
          >
            <i className="bi bi-diagram-3" />
            See the Team Tree
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="report-container p-2 p-md-4">
      <PageHeader
        icon={<i className="bi bi-people-fill"></i>}
        title="Teams"
        subtitle="Create teams, name them, and choose who is in each one"
        /* Counts only. The New Team button was here as well, which made two
           controls for one job — and the tile in the grid is the better of the
           two: it sits where the team will appear, so pressing it and watching
           the form open in that exact cell is one movement instead of a button
           at the top and a result somewhere below. */
        actions={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <StatChip icon="bi-people" tone="purple" label="Teams" value={activeTeams.length} />
            <StatChip icon="bi-person-check" tone="green" label="People" value={totalPeople} />
          </div>
        }
      />

      <div className="position-relative" style={{ minHeight: "200px" }}>
        {teamsStatus === "loading" && !activeTeams.length && (
          <div className="tm-overlay">
            <Loader isLoading />
          </div>
        )}

        <div className="tm-grid">
          {activeTeams.map((t, i) => {
            /* Renaming replaces this team's card with the form, in place.
               Nothing else on the page moves, and the card being edited is the
               card that was clicked. */
            if (form?.teamCode === t.TeamCode) {
              return <TeamFormCard key={t.TeamCode} {...formProps} />;
            }

            const colors = getTeamColors(t.TeamName);
            const count = memberCount(t.TeamCode);

            return (
              <div className="tm-card" key={t.TeamCode} style={{ "--tm-accent": colors.text }}>
                <div className="tm-card__head">
                  <span className="tm-card__short">{t.ShortName}</span>
                  <h6 className="tm-card__name" title={t.TeamName}>
                    {t.TeamName}
                  </h6>
                </div>

                <div className="tm-card__body">
                  <span className="tm-card__stat">
                    <strong>{count}</strong> {count === 1 ? "member" : "members"}
                  </span>
                </div>

                <div className="tm-card__foot">
                  {/* Opens the roster here. It used to send you to the Team
                      Tree, which is the picture, not the place to edit it. */}
                  <button
                    className="tm-link"
                    onClick={() => {
                      setPickedUser("");
                      setPeopleFor({ teamCode: t.TeamCode, teamName: t.TeamName });
                    }}
                    title={canEdit ? "Add or remove people" : "See who is in this team"}
                  >
                    <i className="bi bi-person-plus" /> People
                  </button>

                  {canEdit && (
                    <>
                      <button
                        className="tm-link"
                        onClick={() =>
                          setForm({
                            teamCode: t.TeamCode,
                            original: t.TeamName,
                            teamName: t.TeamName,
                            shortName: t.ShortName,
                            /* Already has one somebody chose — never overwrite
                               it from the name being edited. */
                            shortTouched: true,
                          })
                        }
                      >
                        <i className="bi bi-pencil" /> Rename
                      </button>

                      <span className="tm-order">
                        <button
                          className="tm-icon"
                          disabled={busy || i === 0}
                          title="Move earlier"
                          onClick={() => move(t.TeamCode, -1)}
                        >
                          <i className="bi bi-chevron-up" />
                        </button>
                        <button
                          className="tm-icon"
                          disabled={busy || i === activeTeams.length - 1}
                          title="Move later"
                          onClick={() => move(t.TeamCode, 1)}
                        >
                          <i className="bi bi-chevron-down" />
                        </button>
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {/* The last cell: either the new-team form, or the tile that opens
              it. Always present rather than summoned from the header, so
              "where do I make one" is answered by looking at the page. */}
          {canEdit &&
            (form && !form.teamCode ? (
              <TeamFormCard key="new" {...formProps} />
            ) : (
              <button type="button" className="tm-card tm-card--new" onClick={openNew}>
                <span className="tm-card__plus">
                  <i className="bi bi-plus-lg" />
                </span>
                <span className="tm-card__newlabel">New team</span>
                <span className="tm-card__newsub">
                  {activeTeams.length
                    ? "Name it, and it can take work straight away"
                    : "Start with your first one"}
                </span>
              </button>
            ))}
        </div>

      </div>

      {/* THE ROSTER
          A dialog rather than an expanded card: a roster is a list, an Add box
          and sometimes a question, and none of that fits the 250px cell a team
          card occupies. The create form does fit, which is why that one stayed
          in the grid and this one did not. */}
      {peopleFor && (
        <div
          className="tm-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setPeopleFor(null)}
        >
          <div className="tm-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="tm-modal__head">
              <span
                className="tm-card__short"
                style={{
                  "--tm-accent": getTeamColors(peopleFor.teamName).text,
                }}
              >
                {activeTeams.find((t) => t.TeamCode === peopleFor.teamCode)
                  ?.ShortName || ""}
              </span>
              <div className="tm-modal__titles">
                <h6>{peopleFor.teamName}</h6>
                <p>
                  {memberCount(peopleFor.teamCode)}{" "}
                  {memberCount(peopleFor.teamCode) === 1 ? "person" : "people"} in
                  this team
                </p>
              </div>
              <button
                type="button"
                className="tm-modal__close"
                onClick={() => setPeopleFor(null)}
                aria-label="Close"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="tm-roster">
              {membersOf(peopleFor.teamCode).map((m) => (
                <div className="tm-person" key={m.UserCode}>
                  <span
                    className="tm-person__avatar"
                    style={{
                      background: getTeamColors(peopleFor.teamName).text,
                    }}
                  >
                    {initialsOf(m.UserName)}
                  </span>
                  <span className="tm-person__text">
                    <strong>{m.UserName}</strong>
                    <em>{m.EMail || m.Role || "—"}</em>
                  </span>
                  {/* What they do. Beside the reporting line because the two
                      answer the halves of one question — what is this person,
                      and who are they under. */}
                  {canEdit ? (
                    <PositionSelect
                      value={m.DesignationCode ?? ""}
                      options={designations}
                      disabled={busy}
                      onChange={(code) =>
                        setDesignation(peopleFor.teamCode, m.UserCode, code)
                      }
                    />
                  ) : (
                    <span className="tm-person__role">
                      {m.DesignationName || m.Role}
                    </span>
                  )}

                  {/* Who they sit under, in this team. A select on the row
                      rather than a separate screen: the roster IS the list of
                      possible answers, so the question and its options are in
                      one place. */}
                  {canEdit ? (
                    <select
                      className="tm-person__mgr"
                      value={m.ReportsTo ?? ""}
                      disabled={busy}
                      title={
                        managerNameOf(peopleFor.teamCode, m)
                          ? `Reports to ${managerNameOf(peopleFor.teamCode, m)}`
                          : `Top of ${peopleFor.teamName}`
                      }
                      onChange={(e) =>
                        setManager(peopleFor.teamCode, m.UserCode, e.target.value)
                      }
                    >
                      {managerOptions(peopleFor.teamCode, m).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : managerNameOf(peopleFor.teamCode, m) ? (
                    <span className="tm-person__role">
                      ↳ {managerNameOf(peopleFor.teamCode, m)}
                    </span>
                  ) : null}
                  {canEdit && (
                    <button
                      type="button"
                      className="tm-person__remove"
                      disabled={busy}
                      title={`Remove ${m.UserName} from ${peopleFor.teamName}`}
                      onClick={() =>
                        removeMember(peopleFor.teamCode, m, peopleFor.teamName)
                      }
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  )}
                </div>
              ))}

              {!memberCount(peopleFor.teamCode) && (
                <p className="tm-roster__empty">
                  Nobody in this team yet.
                  {canEdit ? " Add the first person below." : ""}
                </p>
              )}
            </div>

            {canEdit ? (
              <div className="tm-addrow">
                <div className="tm-addrow__field">
                  <SelectInput
                    label=""
                    name={`add-${peopleFor.teamCode}`}
                    value={pickedUser}
                    onChange={(e) => setPickedUser(e.target.value)}
                    options={candidatesFor(peopleFor.teamCode)}
                    placeholder={
                      candidatesFor(peopleFor.teamCode).length
                        ? "Search a person…"
                        : "Everybody is already in this team"
                    }
                  />
                </div>
                <button
                  type="button"
                  className="tm-btn-solid"
                  disabled={!pickedUser || busy}
                  onClick={() => addMember(peopleFor.teamCode)}
                >
                  <i className="bi bi-person-plus" />
                  Add
                </button>
              </div>
            ) : (
              <div className="tm-modal__foot">
                <span className="tm-hint m-0">
                  Read-only — changing a roster is an admin action.
                </span>
              </div>
            )}

            <p className="tm-roster__note">
              The first box is their position, which applies everywhere;
              the second is who they report to, which is what draws the shape
              of the Team Tree. Admins reach every team by role, so they are
              not listed here. Removing somebody keeps the work already
              assigned to them, and moves anyone under them up to their own
              manager.
            </p>
          </div>
        </div>
      )}

      {/* ALREADY IN A TEAM — move, or both?
          On top of the roster, because it is a question that has to be
          answered before anything else can happen. */}
      {canEdit && conflict && (
        <div className="tm-modal tm-modal--over" role="dialog" aria-modal="true">
          <div className="tm-modal__box tm-modal__box--narrow">
            <div className="tm-modal__head">
              <span className="tm-modal__icon">
                <i className="bi bi-people-fill" />
              </span>
              <div className="tm-modal__titles">
                <h6>
                  {conflict.userName} is already in{" "}
                  {conflict.existing.map((t) => t.TeamName).join(" and ")}
                </h6>
                <p>Should they move to {conflict.teamName}, or work in both?</p>
              </div>
            </div>

            <div className="tm-choices">
              {/* Both first: it is the safe one. Nothing leaves a roster, so an
                  admin who clicks without reading loses nothing. */}
              <button
                type="button"
                className="tm-choice"
                disabled={busy}
                onClick={() => addMember(conflict.teamCode, "both")}
              >
                <span className="tm-choice__icon tm-choice__icon--green">
                  <i className="bi bi-diagram-2" />
                </span>
                <span className="tm-choice__text">
                  <strong>Work in both</strong>
                  <em>
                    Stays in {conflict.existing.map((t) => t.TeamName).join(" and ")}{" "}
                    and joins {conflict.teamName}. Can be given work by either.
                  </em>
                </span>
              </button>

              <button
                type="button"
                className="tm-choice"
                disabled={busy}
                onClick={() => addMember(conflict.teamCode, "move")}
              >
                <span className="tm-choice__icon tm-choice__icon--amber">
                  <i className="bi bi-arrow-right-circle" />
                </span>
                <span className="tm-choice__text">
                  <strong>Move to {conflict.teamName}</strong>
                  <em>
                    Leaves {conflict.existing.map((t) => t.TeamName).join(" and ")}.
                    Work already assigned to them stays theirs — only what comes
                    next changes.
                  </em>
                </span>
              </button>
            </div>

            <div className="tm-modal__foot">
              <button
                type="button"
                className="tm-btn-ghost"
                onClick={() => setConflict(null)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;
